/**
 * Résolution des objectifs « effectifs » à partir du profil et du poids courant.
 *
 * Centralise deux logiques auparavant dupliquées dans plusieurs pages :
 *  - le TDEE courant (dépense de maintenance) déduit de la dernière pesée ;
 *  - la résolution auto↔manuel des objectifs (protéines, glucides, lipides, eau).
 */

import { ageFrom } from './datetime';
import { bmrMifflinStJeor, tdee } from './energy';
import { suggestWaterTarget } from './hydration';
import { deriveMacroTargets } from './macro-targets';
import { suggestProteinTarget } from './protein';
import { round } from './statistics';
import type { Profile, WeightEntry } from './types';
import { currentWeight } from './weight-analytics';

/**
 * Dépense énergétique totale courante (kcal/j) d'après la dernière pesée.
 * Renvoie NaN en l'absence de pesée (le caller décide du repli, ex. le seuil).
 */
export function currentTdee(profile: Profile, weights: readonly WeightEntry[]): number {
  if (weights.length === 0) return NaN;
  const bmr = bmrMifflinStJeor(
    currentWeight(weights),
    profile.heightCm,
    ageFrom(profile.birthdate),
    profile.sex,
  );
  return tdee(bmr, profile.activityLevel);
}

export interface ResolvedTargets {
  proteinG: number;
  carbG: number;
  fatG: number;
  waterMl: number;
}

/**
 * Objectifs effectifs (protéines/glucides/lipides/eau) : recalculés depuis le
 * poids courant en mode « auto », repris tels quels en mode « manuel ».
 * @param currentWeightKg poids courant (NaN accepté → retombe sur les valeurs stockées).
 */
export function resolveTargets(profile: Profile, currentWeightKg: number): ResolvedTargets {
  const hasWeight = Number.isFinite(currentWeightKg);

  const proteinG =
    profile.proteinTargetMode === 'auto' && hasWeight
      ? suggestProteinTarget(currentWeightKg, profile.proteinPerKg)
      : profile.proteinTargetG;

  const macros =
    profile.macroTargetMode === 'auto'
      ? deriveMacroTargets(profile.calorieThreshold, proteinG, profile.fatPct)
      : { carbG: profile.carbTargetG, fatG: profile.fatTargetG };

  const waterMl =
    profile.waterTargetMode === 'auto' && hasWeight
      ? suggestWaterTarget(currentWeightKg, profile.waterMlPerKg)
      : profile.waterTargetMl;

  return {
    proteinG: round(proteinG),
    carbG: round(macros.carbG),
    fatG: round(macros.fatG),
    waterMl: round(waterMl),
  };
}
